/**
 * useCopaAlbum — Hook central da feature Copa 2026.
 *
 * Responsabilidades:
 *   - Carrega estado do álbum (prepared aggregate copa_album_state)
 *   - Carrega progresso dos 3 desafios
 *   - Expõe openPack + collate com refetch automático
 *   - Gerencia onboarding (localStorage por jogador)
 *   - Guarda stickers recém-abertos para animação de PackOpen
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'context/AuthContext';
import {
  getAlbum,
  getCopaChallenges,
  getCopaConfig,
  getCampaignPhase,
  getStickerAvailable,
  getPacksPending,
  countLegendariesDone,
  buildAlbumSlots,
  openPack,
  collate,
  substitute,
  CopaAlbumState,
  CopaConfig,
  CampaignPhase,
  PacksPending,
  PackKind,
  AlbumSlot,
  CollatedSticker,
  RepositorySticker,
  Sticker,
} from 'services/copa.service';
import { registerInteractionCampaign } from 'services/actionlog.service';

// ---------------------------------------------------------------------------
// Estado padrão enquanto carrega
// ---------------------------------------------------------------------------
const EMPTY_STATE: CopaAlbumState = {
  album: [],
  repository: [],
  completedCount: 0,
  openedToday: false,
  hasOnboardingPack: false,
  hasOnboarded: false,
  estadioDays: 0,
  mascoteBehaviors: 0,
};

// Onboarding e "abriu hoje" vêm do BACKEND (copa_album_state: hasOnboardingPack / openedToday),
// NUNCA de localStorage — assim o estado é consistente entre sessões/dispositivos.

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export interface UseCopaAlbumResult {
  albumState: CopaAlbumState;
  /** Grade de slots do álbum montada a partir de config.positions + figurinhas coladas */
  albumSlots: AlbumSlot[];
  challenges: any[];
  isLoading: boolean;
  /** true após o 1º carregamento — usar p/ gatear modais sem flash em refetch */
  hasLoadedOnce: boolean;
  /** Stickers do último pacote aberto (para animação de PackOpen) */
  newStickers: Sticker[];
  /** true = primeira vez do usuário (exibir onboarding) */
  isFirstTime: boolean;
  /** true = álbum 100% completo (12 coladas + 3 lendárias) */
  isComplete: boolean;
  /** Lendárias conquistadas (0–3) */
  legendaryCount: number;
  /** Total conquistado p/ o "X/15" = coladas + lendárias */
  conqueredCount: number;
  /** true = jogador já abriu o pacote neste dia calendário (fuso local) */
  alreadyOpenedToday: boolean;
  /**
   * Número de figurinhas disponíveis no pool do jogador para sorteio (RN-29).
   * CTA de abertura só aparece quando stickerAvailable > 0 && pendingCount > 0.
   */
  stickerAvailable: number;
  /**
   * Quantos envelopes o jogador pode abrir agora (daily + rodada dupla + bônus),
   * limitado pelo pool de figurinhas (`stickerAvailable`): cada abertura sorteia 1.
   * = min(entitlements, stickerAvailable). 0 = nada a abrir. (RN-14/15/16/29)
   */
  pendingCount: number;
  /** Fase atual da campanha (derivada da config, fuso America/Sao_Paulo) */
  phase?: CampaignPhase;
  /** Fim da fase atual em ISO (para contagem regressiva) */
  phaseEndAt?: string;
  /**
   * Campanha "no ar": kill-switch (`copa.enabled`) ligado E ainda não encerrada.
   * A modal/widget só devem renderizar quando true (E4 kill-switch / RE-07).
   */
  isCampaignLive: boolean;
  /**
   * Mensagem informativa quando pool está vazio (EC-12).
   * Quando presente e stickers=[], exibir sem animação.
   */
  poolEmptyMessage: string | null;
  handleOpenPack: () => Promise<void>;
  /** Cola a figurinha selecionada (payload completo) na posição escolhida */
  handleCollate: (sticker: RepositorySticker, position: string) => Promise<void>;
  /** Troca a figurinha de uma posição preenchida por outra da Coleção (RF-08/RN-31) */
  handleSubstitute: (current: CollatedSticker, replacement: RepositorySticker) => Promise<void>;
  clearNewStickers: () => void;
  completeOnboarding: () => Promise<void>;
  /**
   * Carrega os desafios das lendárias (abas Álbum/Coleção), sob demanda e uma
   * única vez por sessão/ciclo. Fora isso o load do Dashboard traz só o resumo.
   */
  loadChallenges: () => Promise<void>;
  /**
   * Carrega os envelopes pendentes (abas Pacote/Álbum), sob demanda e uma única
   * vez por sessão. Fora isso o load do Dashboard traz só o resumo.
   */
  loadPacksPending: () => Promise<void>;
  refetch: () => Promise<void>;
  error: string | null;
}

export function useCopaAlbum(cycleId?: string): UseCopaAlbumResult {
  const { user } = useAuth();

  // Elegibilidade (RN-01): participante com extra.actions !== false. Inelegível
  // não dispara nenhum fetch — a UI (modal/widget) já retorna null no render, mas
  // o guard precisa estar AQUI para os effects não saírem (return null não cancela effect).
  const isEligible = user?.extra?.actions !== false;

  const [albumState, setAlbumState] = useState<CopaAlbumState>(EMPTY_STATE);
  const [config, setConfig] = useState<CopaConfig | null>(null);
  const [challenges, setChallenges] = useState<any[]>([]);
  const [newStickers, setNewStickers] = useState<Sticker[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // true após o 1º carregamento concluído. Evita o "flash" do onboarding: refetches
  // posteriores alternam isLoading, mas não devem esconder/reexibir a modal.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Onboarding (intro/tutorial). Concluído = marcador de BACKEND (`hasOnboarded`, da
  // interação onboarding_complete) — sobrevive a logout/login/troca de dispositivo,
  // já que o localStorage é limpo no signOut. `onboardingDismissed` só dispensa na
  // sessão atual (fechamento imediato após "Começar", antes do refetch refletir).
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [stickerAvailable, setStickerAvailable] = useState<number>(0);
  const [packsPending, setPacksPending] = useState<PacksPending>({
    canDaily: false,
    canRodadaDupla: false,
    bonusPending: [],
  });
  const [poolEmptyMessage, setPoolEmptyMessage] = useState<string | null>(null);

  // Backend é a fonte da verdade:
  const alreadyOpenedToday = albumState.openedToday;
  // Mostra a modal de onboarding (tutorial) até concluída. `hasOnboarded` (backend)
  // garante 1× por jogador mesmo após logout; `onboardingDismissed` fecha na hora.
  const isFirstTime = !!user?._id && !albumState.hasOnboarded && !onboardingDismissed;

  // Entitlements: envelopes na fila do jogador (daily + rodada dupla + bônus, RN-14/15/16).
  const entitledPacks =
    (packsPending.canDaily ? 1 : 0) +
    (packsPending.canRodadaDupla ? 1 : 0) +
    packsPending.bonusPending.length;

  // Envelopes REALMENTE abríveis agora: cada abertura sorteia 1 figurinha do pool
  // (copa_sticker_available), então o pool é o teto. Sem esse limite a UI prometia
  // mais envelopes do que há figurinhas — ex.: 2 entitlements com pool=1 mostrava
  // "2 envelopes para abrir" e o 2º caía em pool vazio (EC-12).
  const pendingCount = Math.min(entitledPacks, stickerAvailable);

  // Próximo envelope a abrir, por prioridade: daily → rodada dupla → bônus.
  // (Não há mais pacote de onboarding — RF-04 removido.)
  const nextPack = useCallback((): { kind: PackKind; sourceRecognitionId?: string } | null => {
    if (packsPending.canDaily) return { kind: 'daily' };
    if (packsPending.canRodadaDupla) return { kind: 'rodada_dupla' };
    if (packsPending.bonusPending.length > 0) {
      return { kind: 'bonus', sourceRecognitionId: packsPending.bonusPending[0] };
    }
    return null;
  }, [packsPending]);

  // Grade do álbum montada a partir de config.positions + figurinhas coladas (array plano)
  const albumSlots = useMemo(
    () => buildAlbumSlots(config?.positions ?? [], albumState.album),
    [config?.positions, albumState.album],
  );

  // Lendárias conquistadas (0–3) — challenge-based, não vivem em copa_album__c
  const legendaryCount = useMemo(() => countLegendariesDone(challenges), [challenges]);

  // Total conquistado para o "X/15" = coladas (album) + lendárias conquistadas
  const conqueredCount = albumState.completedCount + legendaryCount;

  // Álbum completo = 12 posições coladas + 3 lendárias = 15
  const isComplete = conqueredCount >= 15;

  // Fase da campanha derivada da config (fuso America/Sao_Paulo)
  const { phase, phaseEndAt } = useMemo(() => getCampaignPhase(config), [config]);

  // Campanha "no ar": kill-switch ligado e ainda não encerrada. Enquanto a config
  // carrega (null), assume no ar para não esconder/piscar a UI durante o fetch.
  const isCampaignLive = config?.enabled !== false && phase !== 'ENCERRADA';

  // ---------------------------------------------------------------------------
  // Carregamento — em camadas (perf, ADR on-demand):
  //   1) loadSummary       → eager, no load do Dashboard. Só o que o WIDGET
  //      precisa (álbum/config/pool). NÃO depende de `cycleId`: assim o effect
  //      dispara UMA vez por load. Antes, `refetch` dependia de `cycleId`, que
  //      resolve de forma assíncrona (undefined → id) e refazia o callback,
  //      re-disparando TODAS as chamadas da campanha a cada load do Dashboard.
  //   2) loadChallenges /
  //      loadPacksPending  → on-demand POR ABA, quando o jogador abre/navega no
  //      álbum. Idempotentes: cada slice carrega UMA vez por sessão/ciclo, então
  //      reabrir o álbum ou alternar abas não refaz as chamadas do resumo.
  //   3) refetch           → recarga completa (após abrir pacote/colar).
  // ---------------------------------------------------------------------------
  const loadSummary = useCallback(async () => {
    if (!user?._id || !isEligible) return;
    try {
      setIsLoading(true);
      setError(null);
      const [state, cfg, available] = await Promise.all([
        getAlbum(),
        getCopaConfig(),
        getStickerAvailable(),
      ]);
      setAlbumState(state);
      setConfig(cfg);
      setStickerAvailable(available);
    } catch (err: any) {
      console.error('[useCopaAlbum] Erro ao carregar resumo:', err);
      setError('Não foi possível carregar o álbum. Tente novamente.');
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  }, [user?._id, isEligible]);

  // Guards de idempotência: cada slice do modal carrega UMA vez por sessão/ciclo.
  const challengesLoadedRef = useRef(false);
  const packsLoadedRef = useRef(false);

  // Desafios são escopados por ciclo: se o ciclo muda, libera novo carregamento.
  useEffect(() => {
    challengesLoadedRef.current = false;
  }, [cycleId]);

  const loadChallenges = useCallback(async () => {
    if (!user?._id || !isEligible || challengesLoadedRef.current) return;
    challengesLoadedRef.current = true;
    try {
      const chal = await getCopaChallenges(cycleId);
      setChallenges(chal);
    } catch (err: any) {
      challengesLoadedRef.current = false; // libera nova tentativa
      console.error('[useCopaAlbum] Erro ao carregar desafios:', err);
    }
  }, [user?._id, cycleId, isEligible]);

  const loadPacksPending = useCallback(async () => {
    if (!user?._id || !isEligible || packsLoadedRef.current) return;
    packsLoadedRef.current = true;
    try {
      const pending = await getPacksPending();
      setPacksPending(pending);
    } catch (err: any) {
      packsLoadedRef.current = false; // libera nova tentativa
      console.error('[useCopaAlbum] Erro ao carregar envelopes pendentes:', err);
    }
  }, [user?._id, isEligible]);

  const refetch = useCallback(async () => {
    if (!user?._id || !isEligible) return;
    try {
      setIsLoading(true);
      setError(null);
      const [state, chal, cfg, available, pending] = await Promise.all([
        getAlbum(),
        getCopaChallenges(cycleId),
        getCopaConfig(),
        getStickerAvailable(),
        getPacksPending(),
      ]);
      setAlbumState(state);
      setChallenges(chal);
      setConfig(cfg);
      setStickerAvailable(available);
      setPacksPending(pending);
      // Recarga completa traz desafios + pendentes frescos: marca como carregados
      // para os loaders por aba não refazerem essas chamadas em seguida.
      challengesLoadedRef.current = true;
      packsLoadedRef.current = true;
    } catch (err: any) {
      console.error('[useCopaAlbum] Erro ao carregar:', err);
      setError('Não foi possível carregar o álbum. Tente novamente.');
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  }, [user?._id, cycleId, isEligible]);

  useEffect(() => {
    if (!user?._id || !isEligible) return;
    loadSummary();
  }, [user?._id, isEligible, loadSummary]);

  // ---------------------------------------------------------------------------
  // Recargas DIRECIONADAS pós-mutação: cada ação atualiza só os slices que muda,
  // em vez de uma recarga completa (3 chamadas copa + desafios). A config nunca
  // muda na sessão, então fica fora. Mantêm os guards por aba coerentes.
  // ---------------------------------------------------------------------------
  const reloadAlbum = useCallback(async () => {
    setAlbumState(await getAlbum());
  }, []);

  const reloadEntitlements = useCallback(async () => {
    setPacksPending(await getPacksPending());
    packsLoadedRef.current = true;
  }, []);

  const reloadChallenges = useCallback(async () => {
    setChallenges(await getCopaChallenges(cycleId));
    challengesLoadedRef.current = true;
  }, [cycleId]);

  // ---------------------------------------------------------------------------
  // Abrir pacote do dia
  // ---------------------------------------------------------------------------
  const handleOpenPack = useCallback(
    async (options?: { kind?: PackKind; sourceRecognitionId?: string; onboarding?: boolean }) => {
      try {
        setError(null);
        setPoolEmptyMessage(null);
        const result = await openPack(options);

        // EC-12: pool vazio → backend retorna { stickers: [], message: string }
        // Não é um erro — nada foi sorteado. Pool = 0 esconde a CTA sem rede.
        if ((result.stickers ?? []).length === 0 && result.message) {
          setPoolEmptyMessage(result.message);
          setStickerAvailable(0);
          return;
        }

        setNewStickers(result.stickers ?? []);
        const kind = options?.onboarding ? 'onboarding' : options?.kind ?? 'daily';
        registerInteractionCampaign('me', 'copa_interaction', {
          type: kind === 'onboarding' ? 'onboarding_pack' : `open_${kind}`,
        });
        // Abrir sorteia 1 figurinha por envelope: decrementa o pool localmente
        // (evita rebuscar copa_sticker_available) e recarrega só o que o backend
        // muda — álbum (repo/openedToday/estadioDays), pendentes (consumiu 1) e
        // desafios (pode ter completado a lendária Estádio). Config fica fora.
        setStickerAvailable(prev => Math.max(0, prev - (result.stickers?.length ?? 0)));
        await Promise.all([reloadAlbum(), reloadEntitlements(), reloadChallenges()]);
      } catch (err: any) {
        // Rejeições podem vir como body.errors (HTTP 200) ou erro HTTP (ex: dup-key 500)
        const httpMsg = err?.response?.data?.message ?? '';
        const msg = (httpMsg || err?.message || '').toString();
        if (msg.toLowerCase().includes('cooldown') || err?.response?.status === 409) {
          setError('Você já abriu seu pacote hoje. Volte amanhã!');
          // Sincroniza o estado de "já abriu hoje" (álbum) e os pendentes.
          await Promise.all([reloadAlbum(), reloadEntitlements()]);
        } else if (msg) {
          setError(msg);
        } else {
          setError('Não foi possível abrir o pacote. Tente novamente.');
        }
        throw err;
      }
    },
    [reloadAlbum, reloadEntitlements, reloadChallenges],
  );

  // Abertura a partir do foil pack: abre o PRÓXIMO envelope da fila
  // (daily → rodada dupla → bônus). Cada chamada abre 1 envelope (1 figurinha).
  const openNextPack = useCallback(async () => {
    const next = nextPack();
    if (!next) return;
    await handleOpenPack(next);
  }, [handleOpenPack, nextPack]);

  // ---------------------------------------------------------------------------
  // Colar figurinha — o trigger precisa do payload completo da figurinha
  // ---------------------------------------------------------------------------
  const handleCollate = useCallback(
    async (sticker: RepositorySticker, position: string) => {
      try {
        setError(null);
        await collate({
          position,
          tipo: sticker.type,
          behavior: sticker.behavior,
          recognitionId: sticker.recognitionId,
          colleagueId: sticker.colleagueId,
        });
        registerInteractionCampaign('me', 'copa_interaction', { type: 'collate' });
        // Colar muda o álbum e pode completar a lendária Taça (álbum completo);
        // NÃO sorteia nem consome envelope, então pool e pendentes ficam como
        // estão. Recarrega só álbum + desafios.
        await Promise.all([reloadAlbum(), reloadChallenges()]);
      } catch (err: any) {
        const httpMsg = err?.response?.data?.message ?? '';
        const msg = (httpMsg || err?.message || '').toString();
        setError(msg || 'Não foi possível colar a figurinha. Tente novamente.');
        throw err;
      }
    },
    [reloadAlbum, reloadChallenges],
  );

  // ---------------------------------------------------------------------------
  // Trocar figurinha de uma posição preenchida (RF-08/RN-31) — PUT in-place.
  // ---------------------------------------------------------------------------
  const handleSubstitute = useCallback(
    async (current: CollatedSticker, replacement: RepositorySticker) => {
      try {
        setError(null);
        await substitute(current, replacement);
        // A troca muda só o álbum (a Coleção é derivada: sorteadas − coladas, então
        // a carta anterior reaparece sozinha). NÃO emite copa_collate nem consome
        // envelope → desafios/pool/pendentes inalterados. A telemetria `substitute`
        // é gravada server-side pela trigger (RN-26). Recarrega só o álbum.
        await reloadAlbum();
      } catch (err: any) {
        const httpMsg = err?.response?.data?.message ?? '';
        const msg = (httpMsg || err?.message || '').toString();
        setError(msg || 'Não foi possível trocar a figurinha. Tente novamente.');
        throw err;
      }
    },
    [reloadAlbum],
  );

  // ---------------------------------------------------------------------------
  // Helpers de UI
  // ---------------------------------------------------------------------------
  const clearNewStickers = useCallback(() => setNewStickers([]), []);

  // Onboarding: "Começar minha convocação" (ou fechar o intro) grava a interação
  // `onboarding_complete` (backend, sobrevive a logout) e dispensa na sessão atual.
  // Não há mais pacote de onboarding; o jogador cai no foil pack e abre o Pacote do Dia/bônus.
  const completeOnboarding = useCallback(async () => {
    registerInteractionCampaign('me', 'copa_interaction', { type: 'onboarding_complete' });
    setOnboardingDismissed(true);
  }, []);

  return {
    albumState,
    albumSlots,
    challenges,
    isLoading,
    hasLoadedOnce,
    newStickers,
    isFirstTime,
    isComplete,
    legendaryCount,
    conqueredCount,
    alreadyOpenedToday,
    stickerAvailable,
    pendingCount,
    poolEmptyMessage,
    phase,
    phaseEndAt,
    isCampaignLive,
    handleOpenPack: openNextPack,
    handleCollate,
    handleSubstitute,
    clearNewStickers,
    completeOnboarding,
    loadChallenges,
    loadPacksPending,
    refetch,
    error,
  };
}
